import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, MailCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";
import { Logo } from "@/components/lembra/Logo";
import { SocialAuthButtons } from "@/components/lembra/SocialAuthButtons";
import { AnimatedReveal } from "@/components/hero/shared/AnimatedReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { emailSchema } from "@/lib/schemas";

// Login's password field intentionally uses a LOOSE schema: just non-empty.
// Existing users may have passwords that predate the stricter 8+letter+number
// rule. Enforcing the strict rule here would lock them out. The server's
// credential check is the authoritative gate.
const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Please enter your password."),
});

const forgotSchema = z.object({
  email: emailSchema,
});

type LoginForm = z.infer<typeof loginSchema>;
type ForgotForm = z.infer<typeof forgotSchema>;

// Supabase v2 returns this code on the error object when a user tries to
// log in before confirming their email. We match on both shapes (new .code
// and legacy .message text) because the error payload has changed across
// client versions.
function isEmailNotConfirmedError(err: Error | null): boolean {
  if (!err) return false;
  const code = (err as Error & { code?: string }).code;
  return code === "email_not_confirmed" || /email not confirmed/i.test(err.message);
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, resetPassword, session, user, loading: authLoading } = useAuth();

  // MS89: if the visitor already has a validated session and somehow
  // landed on /login (e.g. clicked a stale link, hit back-button
  // from a 404, "Sign in" link in nav before the MS50 fix shipped),
  // redirect to /dashboard instead of asking them to sign in again.
  // Wait for auth hydrate + getUser() validation — stale localStorage
  // JWTs must not bounce before the server rejects them.
  useEffect(() => {
    if (!authLoading && session && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, session, user, navigate]);
  // If the user just came from /signup, we show a banner prompting them to
  // confirm the email we just sent, prevents the "I signed up and now
  // nothing works" dead end.
  const pendingConfirmationEmail =
    (location.state as { pendingConfirmationEmail?: string } | null)?.pendingConfirmationEmail;
  const [showPassword, setShowPassword] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState<string | null>(
    pendingConfirmationEmail || null,
  );

  // Forgot password state (dialog visibility + success screen)
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [submittedForgotEmail, setSubmittedForgotEmail] = useState("");

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: pendingConfirmationEmail || "", password: "" },
  });
  const forgotForm = useForm<ForgotForm>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
  });

  const isLoading = loginForm.formState.isSubmitting;
  const forgotLoading = forgotForm.formState.isSubmitting;

  const onLogin = async (values: LoginForm) => {
    const { error } = await signIn(values.email, values.password);

    if (error) {
      if (isEmailNotConfirmedError(error)) {
        setNeedsConfirmation(values.email);
        toast.error("Please confirm your email before logging in. Check your inbox for the link.");
      } else {
        // MS247: was `toast.error(error.message)` — Supabase returns
        // distinct strings for rate-limit, banned-user, and invalid-
        // credentials. Surfacing the raw message lets an attacker map
        // registered emails by error text alone (account enumeration).
        // Normalise to a single generic message for the credentials
        // class. Email-not-confirmed keeps its specific copy above
        // because it's a legitimate user action, not a leak.
        setNeedsConfirmation(null);
        toast.error("Incorrect email or password. Please try again.");
      }
      return;
    }

    toast.success("Welcome back!");
    navigate("/dashboard", { replace: true });
  };

  const onForgot = async (values: ForgotForm) => {
    // MS247: enumeration-safe. The success state copy ("If an account
    // exists for X, you'll receive a reset email") is intentionally
    // ambiguous about whether the email exists — but the previous
    // implementation broke that contract by surfacing the raw error
    // on Supabase-side failures (rate-limit, SMTP hiccup, etc.) and
    // bailing without advancing to the success screen. An attacker
    // could tell "real email + rate-limited" apart from "fake email"
    // by the differing UX. Always advance; log the actual error to
    // console so ops can spot patterns without leaking to the user.
    const { error } = await resetPassword(values.email);
    if (error) {
      console.error("[login] resetPassword failed (suppressed from UI):", error);
    }
    setSubmittedForgotEmail(values.email);
    setForgotSent(true);
  };

  const openForgotDialog = () => {
    forgotForm.reset({ email: loginForm.getValues("email") });
    setForgotSent(false);
    setShowForgot(true);
  };

  if (authLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-cream"
        style={{ background: "linear-gradient(165deg, #FFF8E7 0%, #FBF3D0 40%, #F7E8A4 70%, #FBF3D0 100%)" }}
      >
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center px-4 overflow-hidden"
      style={{ background: "linear-gradient(165deg, #FFF8E7 0%, #FBF3D0 40%, #F7E8A4 70%, #FBF3D0 100%)" }}
    >
      {/* Ambient floating orbs */}
      <div className="pointer-events-none absolute -top-20 -left-20 h-72 w-72 rounded-full bg-butter/50 blur-3xl animate-float" style={{ animationDelay: "0s" }} />
      <div className="pointer-events-none absolute bottom-10 -right-16 h-80 w-80 rounded-full bg-coral/10 blur-3xl animate-float-slow" style={{ animationDelay: "3s" }} />
      <div className="pointer-events-none absolute top-1/2 left-1/4 h-48 w-48 rounded-full bg-sky/15 blur-3xl animate-float" style={{ animationDelay: "1.5s" }} />

      <AnimatedReveal animation="fadeInScale" duration={500} className="w-full max-w-md relative z-10">
        <div className="rounded-2xl bg-white/95 p-8 shadow-xl backdrop-blur-sm" style={{ border: "1px solid rgba(0,0,0,0.06)" }}>
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>

          <div className="mb-6 flex flex-col items-center text-center">
            <h1
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontWeight: 500,
                fontSize: "clamp(1.7rem, 3.4vw, 2.2rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#1D3461",
                margin: "0 0 10px",
                maxWidth: "20ch",
              }}
            >
              Pick up where you{" "}
              <em
                style={{
                  fontStyle: "italic",
                  fontWeight: 500,
                  color: "#C4564A",
                }}
              >
                left off
              </em>
              .
            </h1>
            <p
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                color: "rgba(45,41,38,0.65)",
                margin: 0,
              }}
            >
              Sign in to your Lembra account.
            </p>
          </div>

          {needsConfirmation && (
            <div
              role="status"
              className="mb-6 flex items-start gap-3 rounded-xl bg-sky/15 p-4 text-sm text-navy"
            >
              <MailCheck className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold mb-1">Confirm your email first</p>
                <p className="text-charcoal/70">
                  We sent a confirmation link to <strong>{needsConfirmation}</strong>. Click it, then come back here to log in.
                </p>
              </div>
            </div>
          )}

          <Form {...loginForm}>
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
              <FormField
                control={loginForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-navy">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        className="rounded-xl"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={loginForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-navy">Password</FormLabel>
                      <button
                        type="button"
                        onClick={openForgotDialog}
                        className="text-xs text-navy/60 hover:text-navy transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          className="rounded-xl"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/60 hover:text-charcoal transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full rounded-full"
                disabled={isLoading}
              >
                {isLoading ? "One sec…" : "Log in"}
              </Button>
            </form>
          </Form>

          <SocialAuthButtons mode="login" />

          <p className="mt-6 text-center text-sm text-charcoal/60">
            First time here?{" "}
            <Link to="/signup" className="font-medium text-navy hover:underline">
              Create an account
            </Link>
          </p>
        </div>
      </AnimatedReveal>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-navy">Reset your password</DialogTitle>
            <DialogDescription className="text-charcoal/60">
              {/* Sprint 2.5a /auth-fix, pre-fix the form copy
                  promised "we'll send a reset link" (certainty)
                  while the success state used the security-conscious
                  "If an account exists for X…" (intentional
                  uncertainty so we don't leak which emails are
                  registered). The mismatch read as a small bait-
                  and-switch. New form copy matches the success
                  state's framing while still being warm. */}
              {forgotSent
                ? "Check your inbox. Help is on the way."
                : "Enter your email, if we have you on file, a reset link is on its way. No judgement."}
            </DialogDescription>
          </DialogHeader>

          {forgotSent ? (
            <div className="space-y-4 pt-2">
              <div className="rounded-xl bg-sky/20 p-4 text-center">
                <p className="text-sm text-navy font-medium">
                  If an account exists for <strong>{submittedForgotEmail}</strong>, you'll receive a password reset email shortly.
                </p>
              </div>
              <Button
                variant="secondary"
                className="w-full rounded-full"
                onClick={() => setShowForgot(false)}
              >
                Back to login
              </Button>
            </div>
          ) : (
            <Form {...forgotForm}>
              <form onSubmit={forgotForm.handleSubmit(onForgot)} className="space-y-4 pt-2">
                <FormField
                  control={forgotForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-navy">Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          autoFocus
                          autoComplete="email"
                          className="rounded-xl"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full rounded-full"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? "On its way…" : "Send reset link"}
                </Button>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
